/**
 * Service worker registration, and the one thing the page needs to know about
 * it: whether a newer build is waiting.
 *
 * Deliberately thin. The worker precaches everything at install and serves it
 * from cache afterwards, so the page has nothing to coordinate — except the
 * moment a new build is sitting in the wings, and even then the app asks
 * rather than reloading, because reloading out from under someone who is
 * mid-way through building a preset is a small betrayal.
 */

type Listener = () => void;

let pendingUpdate: (() => void) | null = null;
const listeners = new Set<Listener>();

/** Subscribe to "a new version is ready". Returns an unsubscribe function. */
export function onUpdateReady(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The function that applies the waiting update, or null if there isn't one. */
export function getPendingUpdate(): (() => void) | null {
  return pendingUpdate;
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  // A worker needs a secure context. localhost counts; a LAN IP over plain http
  // does not, which is the usual reason offline "works on my machine" and not
  // on the phone you tested with.
  if (!window.isSecureContext) return;

  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register(new URL("sw.js", document.baseURI), { scope: "./" })
      .then((registration) => {
        if (registration.waiting) announce(registration.waiting);

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            // "installed" while a controller is already running means this is
            // an update rather than a first install.
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              announce(installing);
            }
          });
        });
      })
      .catch(() => {
        // No offline support this session. The app still works; it just needs a
        // connection to start. Not worth interrupting anyone over.
      });
  });
}

function announce(worker: ServiceWorker): void {
  pendingUpdate = () => activate(worker);
  for (const listener of listeners) listener();
}

function activate(worker: ServiceWorker): void {
  worker.postMessage("skip-waiting");
  // Reload once the new worker takes over, not immediately — reloading first
  // would just re-run the old bundle.
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
}
